import { Divider } from 'antd';
import React, { FC, memo, useCallback, useMemo } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { useAppDispatch, useAppSelector } from 'app/config/storeConfig/hooks';
import { getBoxRentalPriceAmount } from 'entities/Box';
import { BulletsTable } from 'shared/ui/BulletsTable';
import { Button } from 'shared/ui/Button';
import { getSelectedBoxes } from '../../model/selectors/getSelectedBoxes';
import { getSelectedEmail } from '../../model/selectors/getSelectedEmail';
import { getSelectedWarehouse } from '../../model/selectors/getSelectedWarehouse';
import { SendOfferRentOptionData, useSendOfferMutation } from 'entities/Lead';
import { showNotification } from 'app/providers/NotificationsProvider';
import { getSelectedLanguage } from '../../model/selectors/getSelectedLanguage';
import { AppLanguage } from 'app/config/i18Config/types';
import { getLocalizedString } from 'shared/utils/helpers/JSONLocalization';
import { TextArea } from 'shared/ui/TextArea';
import { sendOfferModalActions } from '../../model/slice/sendOfferModalSlice';
import { getOfferNote } from '../../model/selectors/getOfferNote';
import { getSizeCodeTypeString } from 'entities/SizeCode';
import { useGetContractsByUserIdQuery } from 'entities/Contract';
import { useGetUserReservationsQuery } from 'entities/User';
import { getSelectedUserId } from '../../model/selectors/getSelectedUserId';
import { LoyaltyUserSetting } from 'entities/RentOption';
import { Popup } from 'shared/ui/Popup';
import { ReactComponent as InfoIcon } from 'shared/assets/icons/InfoIcon.svg';

interface SummaryStepProps {
  onClose: () => void;
}

export const SummaryStep: FC<SummaryStepProps> = memo((props) => {
  const { onClose } = props;

  const { t } = useAppTranslation(['contracts', 'common', 'boxes']);

  const dispatch = useAppDispatch();

  const currencySymbol = useGetCurrencySymbol();

  const selectedWarehouse = useAppSelector(getSelectedWarehouse);
  const selectedEmail = useAppSelector(getSelectedEmail);
  const selectedUnits = useAppSelector(getSelectedBoxes);
  const note = useAppSelector(getOfferNote);
  const language = useAppSelector(getSelectedLanguage);
  const selectedUserId = useAppSelector(getSelectedUserId);

  const { data: userContracts } = useGetContractsByUserIdQuery({ userId: selectedUserId }, { skip: !selectedUserId });
  const { data: userReservations } = useGetUserReservationsQuery({ userId: selectedUserId, isTakenUp: true }, { skip: !selectedUserId });

  const isNewCustomer = useMemo<boolean>(() => {
    return !userContracts?.length && !userReservations?.length;
  }, [userContracts?.length, userReservations?.length]);

  const [sendOffer, { isLoading }] = useSendOfferMutation();

  const offerDetailsBullets = useMemo(
    () => [
      { label: t('Email'), value: selectedEmail },
      {
        label: t('Warehouse'),
        value: `${selectedWarehouse?.name}`,
      },
    ],
    [selectedEmail, selectedWarehouse?.name, t],
  );

  const offerBoxesBullets = useMemo(() => {
    const boxesBullets = selectedUnits.map((box) => {
      return {
        label: (
          <div className="flex gap-3">
            {box.name} ({box.sizeCode.square} {getSizeCodeTypeString(box.sizeCode.sizeCodeType)})
            {box.subtype && (
              <Popup
                description={<div className="max-w-[310px] desktop:max-w-[600px]">{getLocalizedString(box?.specialityDescription)}</div>}
                isDisabled={!getLocalizedString(box?.specialityDescription)}
              >
                <div className="flex items-center space-x-1.5 w-fit">
                  <InfoIcon width={20} height={20} className="[&>path]:stroke-accent cursor-pointer flex-shrink-0" />
                  <span className="text-sm text-accent font-semibold">{t(box.subtype, { ns: 'boxes' })}</span>
                </div>
              </Popup>
            )}
          </div>
        ),
        value: `${getBoxRentalPriceAmount(box)} ${currencySymbol}`,
        emptyValueSymbol: `- ${currencySymbol}`,
      };
    });

    return boxesBullets;
  }, [currencySymbol, selectedUnits, t]);

  const rentOptionData: SendOfferRentOptionData[] = useMemo(() => {
    const rentOptionsMap = new Map<string, SendOfferRentOptionData>();

    selectedUnits.forEach((unit) => {
      if (!unit.rentOptions || unit.rentOptions.length === 0) {
        return;
      }

      const filteredRentOptionsByLoyaltySetting = unit.rentOptions.filter((rentOption) => {
        if (isNewCustomer) {
          console.log('isNewCustomer', isNewCustomer);
          return [LoyaltyUserSetting.ALL, LoyaltyUserSetting.ONLY_NEW].includes(rentOption.loyaltyUserSetting);
        }

        return [LoyaltyUserSetting.ALL, LoyaltyUserSetting.ONLY_OLD].includes(rentOption.loyaltyUserSetting);
      });

      filteredRentOptionsByLoyaltySetting.forEach((rentOption) => {
        const rentOptionId = rentOption.rentOptionId;
        const label = getLocalizedString(rentOption.label, language);
        const description = getLocalizedString(rentOption.description, language);

        if (!rentOptionsMap.has(rentOptionId)) {
          rentOptionsMap.set(rentOptionId, {
            label,
            description,
            units: [unit.name],
          });
        } else {
          const entry = rentOptionsMap.get(rentOptionId)!;
          if (!entry.units.includes(unit.name)) {
            entry.units.push(unit.name);
          }
        }
      });
    });

    return Array.from(rentOptionsMap.values());
  }, [isNewCustomer, language, selectedUnits]);

  const offerRentOptionsBullets = useMemo(() => {
    return rentOptionData.map((entry) => {
      const unitsList = entry.units.join(', ');

      return {
        label: (
          <div>
            <div>{entry.label}</div>
            <div className="text-primary text-sm">({unitsList})</div>
          </div>
        ),
        value: entry.description,
      };
    });
  }, [rentOptionData]);

  const handleSendOffer = useCallback(async () => {
    try {
      if (selectedEmail && selectedWarehouse) {
        const boxId = selectedUnits.map((box) => box.boxId);

        const sendOfferData = {
          email: selectedEmail,
          warehouseId: selectedWarehouse?.warehouseId,
          boxId,
          note,
          language: language || AppLanguage.de,
          rentOptionData,
        };

        await sendOffer(sendOfferData).unwrap();

        showNotification('info', t('Success', { ns: 'common' }), t('Offer has been successfully sent'));

        onClose();
      }
    } catch (error: CustomAny) {
      showNotification('error', t('Error', { ns: 'common' }), t('Error when sending offer'));
    }
  }, [selectedEmail, selectedWarehouse, selectedUnits, note, language, rentOptionData, sendOffer, t, onClose]);

  const noteChange = useCallback(
    (note: string) => {
      dispatch(sendOfferModalActions.setNote(note));
    },
    [dispatch],
  );

  return (
    <div className="flex flex-col">
      <div className="flex justify-between">
        <div className="flex flex-col pl-3">
          <div className="font-semibold text-2xl mb-4">{t('Offer details')}</div>
          <div className="border border-secondaryAccent rounded-lg py-7 px-3 w-[800px]">
            <BulletsTable heading={t('Details')} rows={offerDetailsBullets} theme="clear" />
            <Divider className="my-4" />
            <BulletsTable heading={t('Units')} rows={offerBoxesBullets} theme="clear" />

            <div>
              <Divider className="my-4" />
              {offerRentOptionsBullets.length > 0 ? (
                <BulletsTable heading={t('Rent options')} rows={offerRentOptionsBullets} theme="clear" />
              ) : (
                <div className="mb-3 text-accent font-semibold text-base">{t('No rent options for these sizes!')}</div>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col w-[325px] pr-3">
          <div className="font-semibold text-2xl mb-4">{t('Note')}</div>
          <TextArea bordered placeholder={t('Add your note...')} className="!h-full" onChange={noteChange} rows={8} />
        </div>
      </div>

      <div className="flex self-end mt-8">
        <Button onClick={handleSendOffer} isLoading={isLoading}>
          {t('Send offer')}
        </Button>
      </div>
    </div>
  );
});
